// Bridge the six-outcome DispositionRecords (Framework Mapping) into the Portfolio view's
// ArtifactList shape (handoff §5.1). SINGLE taxonomy (task 1218070214136694): each row carries the
// engine's real `outcome` (Box 1-4 / Consolidate / Decommission / Queue) — the Portfolio no longer
// collapses Box 1/2/3 into "Migrate" and Box 4 into "Document". The legacy 4-box `disposition`
// alias is still populated (from OUTCOME_TO_LEGACY) for the export vocabulary only. No fabricated
// fields — everything derives from the record, so the Portfolio shows the tenant's real ingest.

import type { DispositionRecord, Outcome } from './framework/types';
import { PRIMITIVES } from './framework/fixtures';
import type { ArtifactList, ArtifactRow, ArtifactDetail, Signal } from './types';
import type { Complexity, ArtifactStatus } from './tokens';
import { OUTCOME_ORDER, OUTCOME_TARGET, OUTCOME_TO_LEGACY } from './tokens';

const OUTCOMES = new Set<Outcome>(OUTCOME_ORDER);
const asOutcome = (d: string): Outcome => (OUTCOMES.has(d as Outcome) ? (d as Outcome) : 'queue');
const BOX_NUM: Record<string, 1 | 2 | 3 | 4> = { box1: 1, box2: 2, box3: 3, box4: 4 };
const BAND_PCT: Record<string, number> = { Critical: 85, High: 68, Moderate: 45, Low: 22 };

const statusLabel = (s: string): ArtifactStatus =>
  s === 'auto_confirmed' || s === 'committed' ? 'Approved' : 'Pending';

function complexityOf(r: DispositionRecord): { label: Complexity; pct: number } {
  const b1 = r.factor_vector?.B1;
  const pct = typeof b1 === 'number' ? Math.round(b1 * 100) : (BAND_PCT[r.materiality_band || 'Low'] ?? 30);
  const label: Complexity = pct >= 66 ? 'High' : pct >= 33 ? 'Medium' : 'Low';
  return { label, pct };
}

export function recordsToArtifactList(records: DispositionRecord[]): ArtifactList {
  const items: ArtifactRow[] = records.map((r, i) => {
    const outcome = asOutcome(r.disposition);
    const cx = complexityOf(r);
    return {
      id: r.euc_id || `r${i}`,
      name: r.file,
      meta_line: `${r.dept}${r.materiality_band ? ` · ${r.materiality_band}` : ''}${r.source_type ? ` · ${r.source_type}` : ''}`,
      owner: r.dept,
      assignee: '—',
      timing_cat: r.timing === 'cat1' ? 1 : r.timing === 'cat2' ? 2 : 3,
      box: BOX_NUM[r.disposition],   // 1..4 for the boxes; undefined for consolidate / decommission / queue
      outcome,                       // single taxonomy — the pill + tab filter read this
      disposition: OUTCOME_TO_LEGACY[outcome],   // legacy 4-box alias (export vocabulary only)
      target_state_label: OUTCOME_TARGET[outcome] || '',
      confidence: r.confidence ?? null,   // null → the pill shows no "%", just the outcome (in review)
      complexity: cx.label,
      complexity_pct: cx.pct,
      status: statusLabel(r.status),
    };
  });
  const counts_by_outcome = { box1: 0, box2: 0, box3: 0, box4: 0, consolidate: 0, decommission: 0, queue: 0 };
  for (const it of items) counts_by_outcome[it.outcome] += 1;
  return { items, total: items.length, counts_by_outcome };
}

const NO_PURPOSE =
  'Not captured yet — the purpose line comes from the owner attestation or from extracting the source file.';

function factorSignals(fv: Record<string, number> | undefined): Signal[] {
  if (!fv) return [];
  return Object.entries(fv)
    .filter(([, v]) => typeof v === 'number')
    .map(([k, v]) => {
      const p = PRIMITIVES[k] || PRIMITIVES[`${k}f`];
      return {
        label: p?.name || k,
        value: v.toFixed(2),
        level: (v >= 0.66 ? 'high' : v >= 0.33 ? 'medium' : 'low') as Signal['level'],
        tooltip: p?.detail || `${k} — scored factor (0 to 1).`,
      };
    });
}

export function recordToArtifactDetail(r: DispositionRecord): ArtifactDetail {
  const outcome = asOutcome(r.disposition);
  return {
    id: r.euc_id,
    name: r.file,
    owner: r.dept,
    assignee: '—',
    timing_cat: r.timing === 'cat1' ? 1 : r.timing === 'cat2' ? 2 : 3,
    outcome,                                     // single taxonomy
    disposition: OUTCOME_TO_LEGACY[outcome],     // legacy 4-box alias (export vocabulary only)
    target_state_label: OUTCOME_TARGET[outcome] || '',
    confidence: r.confidence ?? 0,
    rationale: r.reasoning_summary || r.provisional_reason || '',
    purpose: NO_PURPOSE,
    last_modified: '—',
    usage_summary: '—',
    sources: [],
    targets: [],
    transforms: [],
    transform_count: 0,
    counts: { formulas: 0, macros: 0, hardcodes: 0, lookups: 0, total_statements: 0, distinct_rules: 0 },
    logic_rows: [],
    signals: factorSignals(r.factor_vector),
    overlaps: [],
    // Item 5 ("attach-file-to-inventory-row"): real, not a placeholder — an EUC only counts as
    // having a file attached once eucGrouping.js's attachArtifact/createEucFromRun actually
    // persisted the run under this record's `artifacts` (item 3). This function stays pure (no
    // I/O), so it can say WHETHER a file is attached but not fetch that file's lineage/logic
    // detail — LogicDecomposerDashboard.tsx's openArtifact does that async fetch (loadDecomposedRun)
    // and merges it in once this flag is true.
    file_attached: !!(r.artifacts && r.artifacts.length > 0),
    gates_fired: r.gates_fired || [],
  };
}
